import React from "react";
import AssessmentIcon from "@material-ui/icons/Assessment";
import BuildIcon from "@material-ui/icons/Build";
import SpeedIcon from "@material-ui/icons/Speed";
import WarningIcon from "@material-ui/icons/Warning";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { TRAVEL_CYCLE_TARGET_RATE } from "@shared/constants/travel-cycle-causes.enum";
import { TravelCycleSummary } from "@shared/interfaces/travel-cycle-analysis.interface";
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";

import { formatCount, formatPercent, percentOf } from "../../cycleUtils";
import { KpiGrid, KpiCard } from "../../styles";

/** Props for the KpiCards molecule. */
interface KpiCardsProps {
  summary: TravelCycleSummary | null;
  platform: ScreenPlatform;
  /** Click on "Viagens com falha": go to the causes. */
  onFailuresClick?: () => void;
  /** Click on "Hardwares a revisar": go to the hardware list. */
  onHardwareClick?: () => void;
}

/** Accent colors for each KPI card's left border. */
const KPI_COLORS = {
  total: "#002951",
  cycle: "#1a73e8",
  failures: "#ff6c5c",
  hardware: "#ff9800"
};

/**
 * The 4 KPI cards of the travel analysis: travels analyzed, complete cycle against the target,
 * failed travels and hardware to check. The last two are clickable.
 */
const KpiCards: React.FC<KpiCardsProps> = ({ summary, platform, onFailuresClick, onHardwareClick }) => {

  const { t } = useTranslation();
  const placeholder = "—";
  const total = summary?.total ?? 0;
  const complete = summary?.complete ?? 0;
  const rate = percentOf(complete, total);
  const hardware = summary?.hardware ?? [];
  const sensors = hardware.filter((h) => h.kind !== "tracker_without_signal").length;
  const trackers = hardware.length - sensors;
  const vehicles = new Set(hardware.map((h) => h.idVehicle)).size;

  return (
    <KpiGrid platform={platform}>
      <KpiCard accentColor={KPI_COLORS.total}>
        <span className="kpi-bg-icon"><AssessmentIcon fontSize="inherit" /></span>
        <div className="kpi-content">
          <span className="kpi-label">{t(TravelAnalysisMessages.totalAnalyzed)}</span>
          <span className="kpi-value">{summary ? formatCount(total) : placeholder}</span>
          <span className="kpi-secondary">{t(TravelAnalysisMessages.cycleKpiTicketTravels)}</span>
        </div>
      </KpiCard>
      <KpiCard accentColor={KPI_COLORS.cycle}>
        <span className="kpi-bg-icon"><SpeedIcon fontSize="inherit" /></span>
        <div className="kpi-content">
          <span className="kpi-label">{t(TravelAnalysisMessages.cycleKpiComplete)}</span>
          <div className="kpi-value-row">
            <span className="kpi-value">{summary ? formatPercent(rate) : placeholder}</span>
            <span className="kpi-meta-tag">{t(TravelAnalysisMessages.target)}: {TRAVEL_CYCLE_TARGET_RATE}%</span>
          </div>
          <div className="kpi-progress-track">
            <div className="kpi-progress-fill" style={{ width: `${Math.min(rate, 100)}%` }} />
            <div className="kpi-progress-target" style={{ left: `${TRAVEL_CYCLE_TARGET_RATE}%` }} />
          </div>
          <span className="kpi-secondary">{`${formatCount(complete)} ${t(TravelAnalysisMessages.cycleKpiCompleteSub)}`}</span>
        </div>
      </KpiCard>
      <KpiCard accentColor={KPI_COLORS.failures} clickable={!!onFailuresClick} onClick={onFailuresClick}>
        <span className="kpi-bg-icon"><WarningIcon fontSize="inherit" /></span>
        <div className="kpi-content">
          <span className="kpi-label">{t(TravelAnalysisMessages.cycleKpiFailed)}</span>
          <span className="kpi-value">{summary ? formatCount(total - complete) : placeholder}</span>
          <span className="kpi-secondary">
            {`${formatPercent(percentOf(total - complete, total))} ${t(TravelAnalysisMessages.percentOfTotal)}`
              + ` · ${t(TravelAnalysisMessages.cycleKpiSeeCauses)}`}
          </span>
        </div>
      </KpiCard>
      <KpiCard accentColor={KPI_COLORS.hardware} clickable={!!onHardwareClick} onClick={onHardwareClick}>
        <span className="kpi-bg-icon"><BuildIcon fontSize="inherit" /></span>
        <div className="kpi-content">
          <span className="kpi-label">{t(TravelAnalysisMessages.cycleKpiHardware)}</span>
          <span className="kpi-value">{summary ? formatCount(hardware.length) : placeholder}</span>
          <span className="kpi-secondary">
            {`${sensors} ${t(TravelAnalysisMessages.cycleKpiSensors)} · ${trackers} ${t(TravelAnalysisMessages.cycleKpiTrackers)}`
              + ` · ${vehicles} ${t(TravelAnalysisMessages.cycleKpiVehicles)}`}
          </span>
        </div>
      </KpiCard>
    </KpiGrid>
  );
};

export default KpiCards;
